import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  Users, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  UserCheck,
  DollarSign,
  Heart,
  Target,
  Zap,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Clock,
  Star,
  Award,
  ShoppingCart,
  Repeat,
  UserPlus,
  UserMinus
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-6 mb-6 shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left hover:bg-blue-200 p-2 rounded transition-colors"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-600" size={24} />
          <h3 className="text-xl font-bold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-600" size={24} /> : <ChevronDown className="text-blue-600" size={24} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-900 space-y-3 bg-white p-4 rounded border border-blue-200">
          {children}
        </div>
      )}
    </div>
  );
};

const CustomerAnalyticsReport = () => {
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [timeFilter, setTimeFilter] = useState("all"); // all, today, 7days, 14days, 30days

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null; // all time
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, tripsRes, driversRes, rideshareRequestsRes, rideshareTripsRes] = await Promise.all([
        fetch(`${API_URL}/customerdetails/`),
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/rideshare/requests/all`),
        fetch(`${API_URL}/rideshare/trips`)
      ]);

      const [customersData, tripsData, driversData, rideshareRequestsData, rideshareTripsData] = await Promise.all([
        customersRes.json(),
        tripsRes.json(),
        driversRes.json(),
        rideshareRequestsRes.json(),
        rideshareTripsRes.json()
      ]);

      setCustomers(customersData || []);
      setTrips(tripsData || []);
      setDrivers(driversData || []);
      setRideshareRequests(Array.isArray(rideshareRequestsData) ? rideshareRequestsData : []);
      setRideshareTrips(Array.isArray(rideshareTripsData) ? rideshareTripsData : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate customer analytics
  const customerAnalytics = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    // `/trip` is exclusively the private-ride table — every row here is a private trip.
    const filteredTrips = filterTripsByDate(trips, dateRange);

    // Rideshare activity/revenue lives on accepted `rideshare_requests` rows (one per passenger,
    // `accepted_amount` is the fare-of-record), not on `/trip`. This report has no per-driver
    // breakdown, so the `/rideshare/trips` route data isn't needed for a join here.
    const filteredRideshareRequests = (dateRange
      ? rideshareRequests.filter(req => {
          if (!req.created_at) return false;
          const d = new Date(req.created_at);
          return d >= dateRange.start && d <= dateRange.end;
        })
      : rideshareRequests
    ).filter(req => (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0) > 0);

    // Calculate customer metrics using same logic as PerformanceReports, folding rideshare
    // requests (matched via passenger_id) in alongside private trips (matched via cust_id).
    const calculateMetrics = (data, type, tripList, rideshareList) => {
      return data.map(item => {
        // Handle different ID field names for customers vs drivers
        const itemId = type === 'driver' ? item.driver_id : item.customerid; // FIXED: Use customerid instead of cust_id
        const relatedTrips = tripList.filter(trip =>
          type === 'driver' ? trip.driver_id == itemId : trip.cust_id == itemId // FIXED: Match trip.cust_id with customer.customerid
        );
        const relatedRideshareRequests = type === 'customer'
          ? rideshareList.filter(req => req.passenger_id === itemId)
          : [];

        const privateValue = relatedTrips.reduce((sum, trip) =>
          sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0
        );
        const rideshareValue = relatedRideshareRequests.reduce((sum, req) =>
          sum + (parseFloat(req.accepted_amount) || parseFloat(req.offer_amount) || 0), 0
        );

        const tripCount = relatedTrips.length + relatedRideshareRequests.length;
        const totalValue = privateValue + rideshareValue;
        const avgValue = tripCount > 0 ? totalValue / tripCount : 0;

        return {
          ...item,
          tripCount,
          totalValue,
          avgValue,
          relatedTrips,
          relatedRideshareRequests
        };
      });
    };

    const customersWithMetrics = calculateMetrics(customers, 'customer', filteredTrips, filteredRideshareRequests);

    // Calculate retention metrics
    const totalCustomers = customers.length;
    const activeCustomers = customersWithMetrics.filter(c => c.tripCount > 0).length;
    const newCustomers = customersWithMetrics.filter(c => c.tripCount === 1).length;
    const returningCustomers = customersWithMetrics.filter(c => c.tripCount > 1).length;
    const loyalCustomers = customersWithMetrics.filter(c => c.tripCount > 15).length;
    
    // Calculate retention rate (customers with 2+ rides / total active customers)
    const retentionRate = activeCustomers > 0 ? (returningCustomers / activeCustomers) * 100 : 0;
    
    // Calculate repeat rate (customers with 2+ rides / customers with 1+ rides)
    const repeatRate = customersWithMetrics.filter(c => c.tripCount >= 1).length > 0 ?
      (customersWithMetrics.filter(c => c.tripCount >= 2).length / customersWithMetrics.filter(c => c.tripCount >= 1).length) * 100 : 0;

    // Calculate additional metrics for customer analytics
    const customersWithDetailedMetrics = customersWithMetrics.map(customer => {
      const customerTrips = customer.relatedTrips || [];
      const customerRideshareRequests = customer.relatedRideshareRequests || [];

      // Combine private-trip and rideshare-request timestamps into one activity timeline
      const activityDates = [
        ...customerTrips.map(trip => new Date(trip.order_start_datetime)),
        ...customerRideshareRequests.map(req => new Date(req.created_at))
      ].filter(d => !isNaN(d.getTime()));

      // Calculate retention metrics
      const firstTripDate = activityDates.length > 0 ? new Date(Math.min(...activityDates)) : null;
      const lastTripDate = activityDates.length > 0 ? new Date(Math.max(...activityDates)) : null;

      // Calculate days between first and last trip
      const daysActive = firstTripDate && lastTripDate ?
        Math.ceil((lastTripDate - firstTripDate) / (1000 * 60 * 60 * 24)) : 0;

      // Calculate average days between rides
      const sortedTrips = activityDates.slice().sort((a, b) => a - b);

      let totalDaysBetween = 0;
      for (let i = 1; i < sortedTrips.length; i++) {
        totalDaysBetween += Math.ceil((sortedTrips[i] - sortedTrips[i-1]) / (1000 * 60 * 60 * 24));
      }
      const avgDaysBetweenRides = sortedTrips.length > 1 ? totalDaysBetween / (sortedTrips.length - 1) : 0;
      
      // Customer segmentation
      let segment = 'New';
      if (customer.tripCount === 0) segment = 'Inactive';
      else if (customer.tripCount === 1) segment = 'First-Time';
      else if (customer.tripCount >= 2 && customer.tripCount <= 5) segment = 'Occasional';
      else if (customer.tripCount >= 6 && customer.tripCount <= 15) segment = 'Regular';
      else if (customer.tripCount > 15) segment = 'Loyal';
      
      return {
        ...customer,
        firstTripDate,
        lastTripDate,
        daysActive,
        avgDaysBetweenRides,
        segment,
        customerTrips
      };
    });

    return {
      customersWithMetrics: customersWithDetailedMetrics,
      totalCustomers,
      activeCustomers,
      newCustomers,
      returningCustomers,
      loyalCustomers,
      retentionRate,
      repeatRate,
      filteredTrips
    };
  }, [customers, trips, rideshareRequests, timeFilter]);

  // Get top/bottom performers
  const getPerformers = (data, metric, order = 'desc') => {
    return [...data]
      .filter(item => item[metric] > 0)
      .sort((a, b) => order === 'desc' ? b[metric] - a[metric] : a[metric] - b[metric])
      .slice(0, 20);
  };

  // Get inactive customers
  const getInactiveCustomers = (data) => {
    return [...data]
      .filter(item => item.tripCount === 0)
      .sort((a, b) => {
        const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
        const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  const topCustomersByValue = getPerformers(customerAnalytics.customersWithMetrics, 'totalValue', 'desc');
  const topCustomersByTrips = getPerformers(customerAnalytics.customersWithMetrics, 'tripCount', 'desc');
  const topCustomersByAvgValue = getPerformers(customerAnalytics.customersWithMetrics, 'avgValue', 'desc');
  const mostFrequentCustomers = getPerformers(customerAnalytics.customersWithMetrics, 'tripCount', 'desc');
  const inactiveCustomers = getInactiveCustomers(customerAnalytics.customersWithMetrics);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get filter label
  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename) => {
    const csv = [
      ['Rank', 'ID', 'Name', 'Email', 'Phone', 'Trips', 'Total Value', 'Avg Value', 'Segment', 'Days Active', 'Avg Days Between Rides'],
      ...data.map((item, index) => [
        index + 1,
        item.customerid,
        item.name ? `${item.name} ${item.surname || ''}`.trim() : 'N/A',
        item.email,
        item.phone,
        item.tripCount,
        item.totalValue.toFixed(2),
        item.avgValue.toFixed(2),
        item.segment,
        item.daysActive,
        item.avgDaysBetweenRides.toFixed(1)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const CustomerTable = ({ title, data, icon: Icon, metric, isHighest = true }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-6">
      <div 
        className="flex items-center justify-between p-4 bg-gradient-to-r from-green-500 to-teal-600 text-white cursor-pointer"
        onClick={() => toggleSection(title)}
      >
        <div className="flex items-center space-x-3">
          <Icon size={20} />
          <h3 className="text-lg font-semibold">{title}</h3>
          <span className="bg-white/20 px-2 py-1 rounded text-sm">
            {data.length} records
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              exportData(data, `${title.replace(/\s+/g, '_')}.csv`);
            }}
            className="p-2 hover:bg-white/20 rounded transition-colors"
          >
            <Download size={16} />
          </button>
          {expandedSections[title] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      
      {expandedSections[title] && (
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3">Rank</th>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Trips</th>
                  <th className="px-4 py-3">Total Value</th>
                  <th className="px-4 py-3">Avg Value</th>
                  <th className="px-4 py-3">Segment</th>
                  <th className="px-4 py-3">Days Active</th>
                  <th className="px-4 py-3">Avg Days Between</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.customerid} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-500">#{index + 1}</span>
                        {index < 3 && (
                          <span className="text-yellow-500">🏆</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {item.customerid}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {item.name ? `${item.name} ${item.surname || ''}`.trim() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-xs">{item.email || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs">{item.phone || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">
                        {item.tripCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-green-600">
                        ${item.totalValue.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      ${item.avgValue.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.segment === 'Loyal' ? 'bg-purple-100 text-purple-800' :
                        item.segment === 'Regular' ? 'bg-blue-100 text-blue-800' :
                        item.segment === 'Occasional' ? 'bg-green-100 text-green-800' :
                        item.segment === 'First-Time' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.segment}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {item.daysActive}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {item.avgDaysBetweenRides.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Customer Analytics & Retention Report
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Comprehensive customer behavior analysis and retention metrics
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="text-gray-500" size={20} />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {getFilterLabel(timeFilter)}
            </span>
          </div>
        </div>

        {/* Time Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { value: "all", label: "All Time" },
            { value: "today", label: "Today" },
            { value: "7days", label: "Past 7 Days" },
            { value: "14days", label: "Past 14 Days" },
            { value: "30days", label: "Past 30 Days" }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setTimeFilter(filter.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeFilter === filter.value
                  ? "bg-green-600 text-white shadow-lg transform scale-105"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report Tooltip */}
      <ReportTooltip title="Customer Analytics & Retention Report - Business Value & Features">
        <div className="space-y-3">
          <div>
            <strong className="text-blue-900">📊 Report Overview:</strong>
            <p>Comprehensive customer behavior analysis with retention metrics, lifetime value calculations, and customer segmentation for actionable business insights.</p>
          </div>
          
          <div>
            <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Customer Retention:</strong> What is our customer retention rate? How many customers come back for a second ride?</li>
              <li><strong>Customer Value:</strong> Who are our most valuable customers? What is the customer lifetime value (CLV)?</li>
              <li><strong>Customer Segmentation:</strong> How do customers behave differently? What are our customer segments?</li>
              <li><strong>Churn Analysis:</strong> Which customers are at risk of churning? What are the usage patterns?</li>
              <li><strong>Growth Opportunities:</strong> Where should we focus customer acquisition efforts?</li>
            </ul>
          </div>
          
          <div>
            <strong className="text-blue-900">✅ Key Features:</strong>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Retention Metrics:</strong> Retention rate, repeat rate, customer churn analysis</li>
              <li><strong>Customer Segmentation:</strong> New, First-Time, Occasional, Regular, Loyal customer segments</li>
              <li><strong>Lifetime Value:</strong> CLV calculations, total revenue per customer, average order value</li>
              <li><strong>Behavioral Analysis:</strong> Days active, average days between rides, usage patterns</li>
              <li><strong>Top Performers:</strong> Most valuable customers, most frequent riders, highest average spenders</li>
              <li><strong>Export Functionality:</strong> CSV download for detailed customer analysis and CRM integration</li>
            </ul>
          </div>
          
          <div>
            <strong className="text-blue-900">💼 Business Impact:</strong>
            <p>This report provides critical insights for customer relationship management, marketing strategies, and revenue optimization. Help identify high-value customers for VIP programs, at-risk customers for retention campaigns, and opportunities for increasing customer lifetime value.</p>
          </div>
          
          <div>
            <strong className="text-blue-900">🔍 Data Source:</strong>
            <p>Uses REAL data from live API calls to customers, private trips, and rideshare requests endpoints. All metrics are calculated from actual customer activity across both private trips and accepted rideshare requests, with real revenue and behavioral patterns.</p>
          </div>
        </div>
      </ReportTooltip>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{customerAnalytics.totalCustomers}</p>
            </div>
            <Users className="text-blue-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Customers</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{customerAnalytics.activeCustomers}</p>
            </div>
            <UserCheck className="text-green-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Retention Rate</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {customerAnalytics.retentionRate.toFixed(1)}%
              </p>
            </div>
            <Heart className="text-purple-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Repeat Rate</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {customerAnalytics.repeatRate.toFixed(1)}%
              </p>
            </div>
            <Repeat className="text-orange-500" size={24} />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Loyal Customers</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {customerAnalytics.loyalCustomers}
              </p>
            </div>
            <Award className="text-yellow-500" size={24} />
          </div>
        </div>
      </div>

      {/* Report Sections */}
      <div className="space-y-6">
        {/* Customer Value Analysis */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <DollarSign className="mr-2 text-green-500" />
            Customer Value Analysis
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CustomerTable
              title="Top 20 Customers by Total Value"
              data={topCustomersByValue}
              icon={DollarSign}
              metric="totalValue"
              isHighest={true}
            />
            <CustomerTable
              title="Top 20 Customers by Average Trip Value"
              data={topCustomersByAvgValue}
              icon={Target}
              metric="avgValue"
              isHighest={true}
            />
          </div>
        </div>

        {/* Customer Frequency Analysis */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Activity className="mr-2 text-blue-500" />
            Customer Frequency Analysis
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CustomerTable
              title="Top 20 Most Frequent Customers"
              data={mostFrequentCustomers}
              icon={Activity}
              metric="tripCount"
              isHighest={true}
            />
            <CustomerTable
              title="Inactive Customers (Zero Activity)"
              data={inactiveCustomers}
              icon={UserMinus}
              metric="tripCount"
              isHighest={false}
            />
          </div>
        </div>

        {/* Customer Segmentation */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="mr-2 text-purple-500" />
            Customer Segmentation Breakdown
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {['Loyal', 'Regular', 'Occasional', 'First-Time'].map(segment => {
              const segmentData = customerAnalytics.customersWithMetrics
                .filter(c => c.segment === segment)
                .sort((a, b) => b.totalValue - a.totalValue)
                .slice(0, 20);
              
              return (
                <CustomerTable
                  key={segment}
                  title={`Top 20 ${segment} Customers`}
                  data={segmentData}
                  icon={Users}
                  metric="totalValue"
                  isHighest={true}
                />
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerAnalyticsReport;
